import { ContainerStackScreen } from "@Components/ContainerStackScreen"
import { Button } from "@Components/CustomButton/Button"
import { CustomScrollview } from "@Components/CustomScrollView/CustomScrollView"
import { BreeSerifText } from "@Components/CustomText/BreeSerifText"
import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { CustomCard } from "@Components/Diagnostic/CustomCard"
import { VideoRDVCard } from "@Components/Diagnostic/VideoRDVCard"
import { baseColors, dimensions } from "@Constants/baseStyles"
import { DiagnosticEducatifContext } from "@Providers/DiagnosticEducatifProvider"
import { useIsFocused, useNavigation } from "@react-navigation/native"
import { currentDiagnosisAtom } from "@Recoil/Atom/DiagnosisAtom"
import { surveyIsFinishedAtom, surveyProgressAtom, surveyResponsesAtom } from "@Recoil/Atom/surveyAtom"
import { userAtom } from "@Recoil/Atom/userAtom"
import { LocalStorage } from "@Services/sharedPreferences/localStorage"
import React, { useContext, useEffect, useState } from "react"
import { View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { useRecoilState, useRecoilValue } from "recoil"
import { useFetchAllAxis } from "../../Hooks/index"
import { getAxisIcon } from "./DiagnosticLogic"
import { useSurveyHandler } from "./Logic"
import { UserProgramContext } from "@Providers/ProgramProvider"
import { SALUTANCE } from "@Services/AxiosInstance"

export const DiagnosticEducatifList = () => {
    const user = useRecoilValue(userAtom)
    const isFocused = useIsFocused()
    const navigation = useNavigation()
    const { setFetchingDiagEduc } = useContext(DiagnosticEducatifContext)
    const currentEducationalDiagnosis = useRecoilValue(currentDiagnosisAtom)
    const [fetchingEvent, setFetchingEvent] = useState(false)
    const [lastVisitedAxeIndex, setLastVisitedAxeIndex] = useState(0)
    const [fetchingAxisSurveys, setFetchingAxisSurveys] = useState(false)
    const [resetingResponse, setResetingResponse] = useState(false)
    const [disableToResumeBtn, setDisableToResumeBtn] = useState(false)
    const progress = useRecoilValue(surveyProgressAtom)
    const [surveyIsFinished, setSurveyIsFinished] = useRecoilState(surveyIsFinishedAtom)
    const [responseGlobal, setResponseGlobal] = useRecoilState(surveyResponsesAtom)

    const { preselectedPatientProgram } = useContext(UserProgramContext)

    const { fetchAllAxis, loading, data, fetchAllAxisSurveyResponsesById } = useFetchAllAxis({ user })
    const { fetchEvents } = useSurveyHandler({
        user,
        setFetchingDiagEduc,
        setLoading: setFetchingEvent
    })
    const diagnosis = data?.length > 0 ? data : []
    const gotoSurvery = ({ axis, bgColor, forVideoPoint = false, axeIndex }) => {
        navigation.navigate("Survey", {
            axisId: axis.id,
            axisName: axis.name,
            bgColor,
            allAxis: diagnosis,
            forVideoPoint,
            axeIndex
        })
    }

    useEffect(() => {
        const abortController = new AbortController()
        fetchAllAxis((hasSurvey = 1))
        fetchEvents()
        fetchAllAxisSurveyResponsesById()

        return () => {
            abortController.abort()
        }
    }, [isFocused])

    useEffect(() => {
        LocalStorage.getSurveyResponse()
            .then((surveyResponse) => {
                const empty = Object.keys(surveyResponse).length === 0
                !empty && setResponseGlobal(surveyResponse)
                setDisableToResumeBtn(empty)
            })
            .catch(() => {})
    }, [isFocused])

    useEffect(() => {
        LocalStorage.getNoticeConsentStatus()
            .then((currentNoticeConstentStatus) => {
                const hasConsent = currentNoticeConstentStatus?.noticeConsent
                if (SALUTANCE) {
                    if (!hasConsent) {
                        navigation.navigate("NoticeConsent")
                    } else {
                        navigation.navigate("DiagnosticEducatifList")
                    }
                }
            })
            .catch(() => {
                console.log({ errorGetingNoticeStatusConstent: "errorGetingNoticeStatusConstent" })
            })
    }, [SALUTANCE])

    useEffect(() => {
        getAxisToContinue()
        if (progress?.length > 0 && progress?.length == diagnosis?.length) {
            let unfinished = progress.filter((item) => item.percent < 100)
            setSurveyIsFinished(unfinished.length === 0)
        }
    }, [progress])

    const getAxisToContinue = () => {
        let axisToContinue = progress.filter((item) => item.percent < 100).sort((a, b) => a.axisId - b.axisId)
        if (axisToContinue.length > 0) {
            return diagnosis?.filter((item) => item.id === axisToContinue[0].axisId)[0]
        }
        return null
    }

    const contentColor = (i) => (i % 2 === 0 ? baseColors.orange.primary : baseColors.green.primary)

    useEffect(() => {
        LocalStorage.getLastAxeSurvey().then((res) => {
            setLastVisitedAxeIndex(+res ?? 0)
        })
    }, [isFocused])

    const handleToResume = () => {
        setResetingResponse(false)
        gotoSurvery({
            axis: diagnosis?.[lastVisitedAxeIndex ?? 0],
            bgColor: contentColor(lastVisitedAxeIndex),
            axeIndex: lastVisitedAxeIndex ?? 0
        })
    }

    const navigateToRdvVideo = () =>
        gotoSurvery({
            axis: { name: "Rendez-vous", id: diagnosis[diagnosis.length - 1].id },
            bgColor: baseColors.black,
            forVideoPoint: true
        })

    const hasNoDE = currentEducationalDiagnosis === null
    const enableReprendreBtn = Object.keys(responseGlobal).length > 0 && hasNoDE
    const programName = preselectedPatientProgram?.[0]?.programTitle ?? ""
    const title = preselectedPatientProgram?.[0]?.programId > 0 ? programName : "Diagnostic Éducatif"

    return (
        <ContainerStackScreen>
            <CustomScrollview paddingBottom={dimensions.fullHeight / 10}>
                <BreeSerifText fontSize={22} color={baseColors.green.primary}>
                    {title}
                </BreeSerifText>
                <View style={{ height: 20 }} />
                <OpenSansText fontSize={14}>
                    Vous remplissez un questionnaire pour le diagnostic éducatif du programme {programName}. Vos
                    réponses sont automatiquement sauvegardées dès que vous passez à la question suivante.
                </OpenSansText>
                <View style={{ height: 20 }} />

                {!loading && !hasNoDE && (
                    <VideoRDVCard
                        disabled={hasNoDE}
                        axisId={6}
                        key={6}
                        onPress={navigateToRdvVideo}
                        surveyIsFinished={surveyIsFinished}
                        backgroundColor={"rgba(13, 68, 71,0.5)"}
                        ellipseColor={"rgba(173, 183, 184,0.5)"}
                        svgIcon={getAxisIcon(6)}
                        title={"Rendez-vous"}
                    />
                )}

                {loading ? (
                    <ActivityIndicator style={{ marginTop: 50 }} color={baseColors.orange.primary} size={"large"} />
                ) : (
                    diagnosis?.map((item, i) => (
                        <CustomCard
                            key={i}
                            axisId={item.id}
                            onPress={() => gotoSurvery({ axis: item, bgColor: contentColor(i), axeIndex: i })}
                            backgroundColor={contentColor(i)}
                            ellipseColor={i % 2 === 0 ? baseColors.orange.secondary : baseColors.green.secondary}
                            svgIcon={getAxisIcon(item.id)}
                            title={item.name}
                            setFetchingAxisSurveys={setFetchingAxisSurveys}
                        />
                    ))
                )}

                {!loading && enableReprendreBtn && (
                    <Button
                        text="Reprendre"
                        width="80%"
                        onPress={handleToResume}
                        disabled={disableToResumeBtn || fetchingAxisSurveys}
                        backgroundColor={baseColors.green.primary}
                        labelColor={"white"}
                        marginTop={10}
                        marginBottom={5}
                        style={{ marginBottom: 150, marginTop: 50 }}
                    />
                )}
            </CustomScrollview>

            {/* {isCpts && <MentionProfessionalTextField />} */}
        </ContainerStackScreen>
    )
}
